import React from 'react';

interface VoiceRecordingModalProps {
    isOpen: boolean;
    onStartRecording: () => void;
    onStopRecording: () => void;
    onCancelRecording: () => void;
    transcriptionText: string;
    newMessage: string;
    isVoiceRecording: boolean;
    voiceConnectionStatus: string;
    recordingElapsedSec: number;
}

const VoiceRecordingModal: React.FC<VoiceRecordingModalProps> = ({
    isOpen,
    onStartRecording,
    onStopRecording,
    onCancelRecording,
    transcriptionText,
    newMessage,
    isVoiceRecording,
    voiceConnectionStatus,
    recordingElapsedSec
}) => {
    if (!isOpen) return null;

    const formatTime = (sec: number) => {
        const s = Math.max(0, Math.floor(sec));
        const mm = String(Math.floor(s / 60)).padStart(2, '0');
        const ss = String(s % 60).padStart(2, '0');
        return `${mm}:${ss}`;
    };

    const getStatusText = () => {
        switch (voiceConnectionStatus) {
            case 'connecting':
                return '正在连接语音服务...';
            case 'connected':
                return !isVoiceRecording ? '准备录音' : '正在录音';
            case 'recording':
                return '正在录音';
            case 'error':
                return '连接失败';
            case 'stopped':
                return '录音完成';
            case 'disconnected':
                return '准备连接';
            default:
                return '初始化中...';
        }
    };

    const getHintText = () => {
        switch (voiceConnectionStatus) {
            case 'connecting':
                return '正在连接语音服务，请稍候...';
            case 'connected':
                return !isVoiceRecording ? '自动开始录音，请开始说话' : '正在录音中，点击停止';
            case 'recording':
                return '正在录音中，点击麦克风停止';
            case 'error':
                return '语音服务连接失败，请检查网络或重试';
            case 'disconnected':
                return '准备连接语音服务...';
            default:
                return '初始化语音功能...';
        }
    };

    const getMicrophoneButtonClass = () => {
        if (isVoiceRecording) {
            return 'bg-red-500 scale-110 shadow-2xl microphone-recording';
        }
        switch (voiceConnectionStatus) {
            case 'connected':
                return 'bg-green-500 hover:bg-green-600 hover:scale-105 shadow-xl';
            case 'connecting':
                return 'bg-yellow-500 shadow-xl status-connecting';
            case 'error':
                return 'bg-red-500 shadow-xl';
            default:
                return 'bg-gray-500 shadow-xl';
        }
    };

    return (
        <>
            {/* 深色遮罩层 */}
            <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex flex-col items-center justify-center">
                {/* 顶部转录文本框 - 仿微信绿色气泡 */}
                {(transcriptionText || newMessage) && (
                    <div className="mb-8 mx-4 max-w-xs voice-bubble-enter">
                        <div className="bg-green-400 rounded-lg px-4 py-3 relative transcription-bubble">
                            {/* 气泡尾巴 */}
                            <div className="absolute bottom-0 right-4 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-green-400 transform translate-y-full"></div>

                            {/* 转录文本内容 - 固定高度可滚动 */}
                            <div className="text-black h-32 overflow-y-auto pr-1">
                                {transcriptionText && (
                                    <div className="mb-2">
                                        <div className="text-xs opacity-75 mb-1">正在识别...</div>
                                        <div className="font-medium break-words">{transcriptionText}</div>
                                    </div>
                                )}
                                {newMessage && (
                                    <div>
                                        <div className="text-xs opacity-75 mb-1">已识别</div>
                                        <div className="font-medium break-words">{newMessage}</div>
                                    </div>
                                )}
                            </div>

                            {/* 声波动画 */}
                            {isVoiceRecording && (
                                <div className="flex items-center justify-end mt-2 space-x-1">
                                    {[...Array(7)].map((_, index) => (
                                        <div
                                            key={index}
                                            className="w-1 bg-black opacity-60 rounded-full voice-wave-bar"
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 中央状态区域 */}
                <div className="flex flex-col items-center mb-12">
                    {/* 状态文字 */}
                    <div className="text-white text-lg font-medium mb-2">
                        {getStatusText()}
                    </div>

                    {/* 录音计时与进度 */}
                    <div className="text-white text-sm opacity-90 mb-6">
                        {formatTime(recordingElapsedSec)} / 01:00
                        <div className="w-48 h-1 bg-white bg-opacity-30 rounded mt-2 overflow-hidden">
                            <div
                                className="h-full bg-white"
                                style={{ width: `${Math.min(100, (recordingElapsedSec / 60) * 100)}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* 大麦克风图标和波纹动画 */}
                    <div className="relative">
                        {/* 波纹动画 - 仅在录音时显示 */}
                        {isVoiceRecording && (
                            <>
                                {[...Array(3)].map((_, index) => (
                                    <div
                                        key={index}
                                        className="absolute inset-0 rounded-full bg-white voice-ripple"
                                        style={{ animationDelay: `${index * 0.5}s` }}
                                    />
                                ))}
                            </>
                        )}

                        {/* 麦克风按钮 */}
                        <button
                            onClick={isVoiceRecording ? onStopRecording : onStartRecording}
                            className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-200 ${getMicrophoneButtonClass()}`}
                        >
                            <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* 底部操作区域 */}
                <div className="flex items-center justify-between w-full max-w-sm px-8">
                    {/* 取消按钮 */}
                    <button
                        onClick={onCancelRecording}
                        className="w-16 h-16 rounded-full bg-gray-600 bg-opacity-80 flex items-center justify-center hover:bg-opacity-100 transition-all duration-200 button-tap"
                    >
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* 转文字按钮 */}
                    <button
                        onClick={onStopRecording}
                        className="w-16 h-16 rounded-full bg-gray-200 bg-opacity-90 flex items-center justify-center hover:bg-opacity-100 transition-all duration-200 button-tap"
                    >
                        <span className="text-gray-800 font-bold text-lg">文</span>
                    </button>
                </div>

                {/* 使用提示 */}
                <div className="absolute bottom-8 left-0 right-0 text-center">
                    <div className={`text-white text-sm opacity-75 ${voiceConnectionStatus === 'recording' ? 'slide-cancel-hint' : ''}`}>
                        {getHintText()}
                    </div>
                </div>
            </div>
        </>
    );
};

export default VoiceRecordingModal;
